import { jsx as _jsx } from "react/jsx-runtime";
import React, { createContext, useCallback, useContext, useEffect, useState, } from "react";
import { useAuth } from "./AuthProvider";
import { setFrontendMode } from "@/shared/api/auth";
import { useUserStore } from "@/entities/user/model";
const GenerationModeContext = createContext(undefined);
/** Режим по умолчанию: AB `frontend_mode_v2` остановлен в пользу чата. */
const DEFAULT_MODE = "chat";
/** Кэш последнего ответа бэка — чтобы первый рендер сразу был в нужном режиме. */
const CACHE_KEY = "frontend_mode_cache";
/** Старый ключ локального переключателя — больше не источник истины. */
const LEGACY_STORAGE_KEY = "songGenerationMode";
function isMode(v) {
    return v === "classic" || v === "chat";
}
function readCache() {
    try {
        const raw = localStorage.getItem(CACHE_KEY);
        if (!raw)
            return null;
        const parsed = JSON.parse(raw);
        if (isMode(parsed.assigned) && isMode(parsed.current)) {
            return { assigned: parsed.assigned, current: parsed.current };
        }
    }
    catch {
        /* ignore */
    }
    return null;
}
function writeCache(mode) {
    try {
        localStorage.setItem(CACHE_KEY, JSON.stringify(mode));
        localStorage.setItem(LEGACY_STORAGE_KEY, mode.current);
    }
    catch {
        /* ignore */
    }
}
function readLegacyFallback() {
    try {
        const saved = localStorage.getItem(LEGACY_STORAGE_KEY);
        if (isMode(saved))
            return saved;
    }
    catch {
        /* ignore */
    }
    return DEFAULT_MODE;
}
export const GenerationModeProvider = ({ children, }) => {
    const { user, isAuthenticated, isLoading: authLoading } = useAuth();
    const cached = readCache();
    const [generationMode, setGenerationModeState] = useState(() => cached?.current ?? readLegacyFallback());
    const [isModeReady, setIsModeReady] = useState(Boolean(cached));
    const applyMode = useCallback((mode) => {
        writeCache(mode);
        setGenerationModeState(mode.current);
        setIsModeReady(true);
    }, []);
    // Синхронизация с бэком после готовности auth. Назначения варианта больше
    // нет (AB `frontend_mode_v2` остановлен): у аккаунта берём сохранённый выбор
    // из /users/me, у гостя и анонима — кэш прошлого ручного выбора либо дефолт
    // `chat`. Probe-POST, который раньше выбивал гостю вариант, убран — это был
    // лишний запрос на каждом первом визите.
    useEffect(() => {
        if (authLoading)
            return;
        if (isAuthenticated && user?.frontend_mode) {
            applyMode(user.frontend_mode);
            return;
        }
        setIsModeReady(true);
    }, [
        authLoading,
        isAuthenticated,
        user?.id,
        user?.frontend_mode?.assigned,
        user?.frontend_mode?.current,
        applyMode,
    ]);
    const setGenerationMode = useCallback((mode) => {
        if (mode === generationMode)
            return;
        // Оптимистично обновляем UI.
        setGenerationModeState(mode);
        void setFrontendMode(mode)
            .then((res) => {
            applyMode(res);
            useUserStore.getState().updateUser({ frontend_mode: res });
        })
            .catch((e) => {
            console.error("frontend_mode switch failed:", e);
            // Откат к кэшу/прежнему, если бэк отклонил.
            const cachedMode = readCache();
            if (cachedMode) {
                setGenerationModeState(cachedMode.current);
            }
        });
    }, [generationMode, applyMode]);
    const toggleGenerationMode = useCallback(() => {
        setGenerationMode(generationMode === "classic" ? "chat" : "classic");
    }, [generationMode, setGenerationMode]);
    return (_jsx(GenerationModeContext.Provider, { value: {
            generationMode,
            setGenerationMode,
            toggleGenerationMode,
            isModeReady,
        }, children: children }));
};
export const useGenerationMode = () => {
    const context = useContext(GenerationModeContext);
    if (context === undefined) {
        throw new Error("useGenerationMode must be used within a GenerationModeProvider");
    }
    return context;
};
